import { json, jsonParseLinter } from '@codemirror/lang-json';
import { linter, lintGutter } from '@codemirror/lint';
import { EditorView } from '@codemirror/view';
import { oneDark } from '@codemirror/theme-one-dark';
import { basicSetup } from '@uiw/codemirror-extensions-basic-setup';
import type { Extension } from '@codemirror/state';
import type { JsonCodeEditorColors } from '../../theme/codeEditorColors';

const CODE_FONT = 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace';

export function buildJsonCodeMirrorExtensions(
  colors: JsonCodeEditorColors,
  readOnly: boolean,
): Extension[] {
  const extensions: Extension[] = [
    basicSetup({
      lineNumbers: true,
      foldGutter: true,
      highlightActiveLine: !readOnly,
      highlightActiveLineGutter: !readOnly,
      bracketMatching: true,
      closeBrackets: !readOnly,
      autocompletion: false,
      indentOnInput: !readOnly,
    }),
    json(),
    EditorView.theme(
      {
        '&': {
          backgroundColor: colors.panelBg,
          height: '100%',
        },
        '.cm-scroller': {
          fontFamily: CODE_FONT,
          fontSize: '13px',
          lineHeight: '1.6',
          backgroundColor: colors.panelBg,
        },
        // Do not set background on .cm-content — it covers the selection layer.
        '.cm-content': {
          padding: '16px 0',
        },
        '.cm-gutters': {
          backgroundColor: colors.panelBg,
          borderRight: `1px solid ${colors.border}`,
        },
        '&.cm-focused': {
          outline: 'none',
        },
        '.cm-selectionBackground': {
          backgroundColor: colors.isDark ? '#2c313a' : '#d9d9d9',
        },
        '&.cm-focused > .cm-scroller > .cm-selectionLayer .cm-selectionBackground': {
          backgroundColor: colors.isDark ? '#3e4451' : '#b3d4fc',
        },
      },
      { dark: colors.isDark },
    ),
    EditorView.lineWrapping,
  ];

  if (colors.isDark) {
    extensions.push(oneDark, EditorView.darkTheme.of(true));
  }

  if (!readOnly) {
    extensions.push(linter(jsonParseLinter()), lintGutter());
  }

  return extensions;
}
